import _ from 'lodash';

import { postJson } from '@zapier/toolbox-browser-fetch';
import {
  EntityActions,
  buildEntityActions,
  buildEntityApi,
  buildEntitySelectors,
} from '@zapier/toolbox-redux-framework';

import type { IApi, ISelectors, IActions } from './types';

export const type = 'partnerProfile';

export const api: IApi = buildEntityApi(
  type,
  () => `/api/platform/cli/profile/`,
  (entityApi: IApi) =>
    (entityApi.updateProfile = (_id, payload, getState) => {
      return postJson(entityApi.modelEndpoint(null, getState), payload);
    })
);

export const selectors: ISelectors = buildEntitySelectors(
  type,
  (sel: ISelectors) => {
    sel.jobTitle = entity => sel.dataItem('job_title', entity) ?? '';

    sel.roles = entity => sel.dataItem('roles', entity) ?? [];

    sel.details = entity => {
      const data = sel.data(entity);

      return {
        firstName: data?.first_name,
        lastName: data?.last_name,
        jobTitle: data?.job_title,
        roles: data?.roles,
      };
    };
    sel.formError = entity =>
      sel.isFailure(entity) ? entity.state.statusMessages : [];

    sel.hasPartnerRoles = entity => {
      const roles = sel.roles(entity);
      const activeRoles = roles.find(role => role.checked === true);

      return !!activeRoles;
    };

    sel.hasPartnerProfile = entity => {
      const roles = sel.roles(entity);
      return roles.some(role => role.checked);
    };
  }
);

export const actions: IActions = buildEntityActions(
  type,
  selectors,
  api,
  (entityActions: IActions) => {
    entityActions.acceptAdminOrCollaboratorInvite = (
      profileData,
      appId,
      appType
    ) => (dispatch, getState) => {
      const { id } = selectors.all.first(getState()).data;
      dispatch(entityActions.updateEntityData(profileData, id));
      const {
        roles,
        firstName: first_name,
        invitationType: invitation_type,
        jobTitle: job_title,
        lastName: last_name,
        tos: tos_agreement,
        optIn: opted_in,
      } = profileData;

      return dispatch(
        EntityActions.saveEntity(
          type,
          () => {
            return {
              first_name,
              last_name,
              job_title,
              roles,
              tos_agreement,
              memberships: [
                {
                  opted_in,
                  type: invitation_type,
                  accept_invite: true,
                  app: { id: appId, type: appType },
                },
              ],
            };
          },
          api.updateProfile,
          {},
          id
        )
      );
    };

    entityActions.acceptOnboardingInvite = userData => (dispatch, getState) => {
      const { id } = selectors.all.first(getState()).data;

      dispatch(
        entityActions.updateEntityData(
          {
            roles: userData.roles,
            tos_agreement: userData.tos,
            job_title: userData.jobTitle,
          },
          id
        )
      );

      return dispatch(
        EntityActions.saveEntity(
          type,
          () => ({
            roles: userData.roles,
            tos_agreement: userData.tos,
            job_title: userData.jobTitle,
          }),
          api.updateProfile,
          {},
          id
        )
      );
    };

    entityActions.createPartnerProfile = profileData => async (
      dispatch,
      getState
    ) => {
      const TEMPORARY_ENTITY_NAME = 'new-partner-profile';

      const messages = {
        failure: 'Something went wrong, please try again',
        success:
          'Your profile has been saved. You can update your profile and email settings in your',
      };

      const data = {
        roles: profileData.roles,
        tos_agreement: profileData.tos,
        job_title: profileData.jobTitle,
      };

      await dispatch(
        entityActions.createAndSaveEntity(messages, data, TEMPORARY_ENTITY_NAME)
      );

      const newEntity = selectors.all.entityByCid(
        TEMPORARY_ENTITY_NAME,
        getState()
      );

      if (selectors.isSuccess(newEntity)) {
        // Delete temporary partner profile entities after successful save action.
        dispatch(entityActions.deleteCollectionEntities('null'));
        dispatch(entityActions.deleteCollectionEntities(TEMPORARY_ENTITY_NAME));
      }
    };
  }
);
